import {
  useEffect,
  useRef,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  Box,
  Image,
  Skeleton,
  Stack,
  Text
} from '@chakra-ui/react'
import { COLORS } from 'utils/styles'
import { ImagesPng } from 'assets/images'
import { motion } from 'framer-motion'
import {
  getEventComments,
  likeEventComment,
  markersSelectors,
  setEventCommentLikeOngoing,
  setEventCommentTree,
  setEventComments,
  usersSelectors
} from 'store'
import {
  EditIcon2,
  LikeIcon,
  CommentIcon,
  RepostIcon,
  ShareIcon
} from 'assets/icons'
import { LoadingSpinier } from 'common/Loading'
import { PENDING } from 'utils/constants'
import {
  isNotMobile,
  postTypeStatus
} from 'utils/helpers'
import { LikeButtonAnimation } from './styled'

const MotionStack = motion(Stack)

export const CommentsList = ({
  GoToMarker,
  setEditCommentData
}) => {
  const {
    activeProfile,
    eventCommentTree,
    eventComments,
    getEventCommentsStatus
  } = useSelector(usersSelectors)
  const { eventData } = useSelector(markersSelectors)

  const dispatch = useDispatch()
  const [animate, setAnimate] = useState([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [oldCommentsCount, setOldCommentsCount] = useState(0)
  const [loadedProfileImage, setLoadedProfileImage] = useState({})
  const observer = useRef()
  const secondToLastItem = useRef(null)

  const handleProfileImage = (postId) => {
    setTimeout(() => {
      setLoadedProfileImage((prev) => ({
        ...prev,
        [postId]: true
      }))
    }, 500)
  }

  const animateLike = (id) => {
    setAnimate((prevState) => ({
      ...prevState,
      [id]: !prevState[id]
    }))

    setTimeout(() => {
      setAnimate((prevState) => ({
        ...prevState,
        [id]: !prevState[id]
      }))
    }, 500)
  }

  useEffect(() => {
    observer.current = new IntersectionObserver((entries) => {
      // The "10" is the limit set on the backend DB query
      if (
        entries[0].isIntersecting
        && eventComments.length - oldCommentsCount >= 10
      ) {
        observer.current.unobserve(secondToLastItem.current)
        setLoadingMore(true)
        setOldCommentsCount(eventComments.length)
        dispatch(getEventComments({
          event_id: eventData?.id,
          lastItemId: eventComments.at(-1).id
        }))
      }
    })

    const currentObserver = observer.current
    if (
      secondToLastItem.current
      && eventComments.length - oldCommentsCount >= 10
    ) {
      currentObserver.observe(secondToLastItem.current)
    }

    setLoadingMore(false)

    return () => {
      if (secondToLastItem.current) {
        observer.current.disconnect()
      }
    }
  }, [eventComments.length])

  useEffect(() => {
    setOldCommentsCount(0)
  }, [eventCommentTree])

  return (
    <Stack>
      {
        getEventCommentsStatus === PENDING && !eventComments.length
          ? (
            <Stack
              marginTop="100px"
              margin="75px 0 55px 0"
            >
              <LoadingSpinier />
            </Stack>
          ) : ''
      }
      {
        eventComments.length
          ? eventComments.map((item, index) => (
            <MotionStack
              key={item.id}
              initial={{ x: isNotMobile() ? '70svw' : 450 }}
              animate={{ x: 0 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 60
              }}
              ref={
                eventComments.length - 2 === index
                  ? secondToLastItem
                  : null
              }
              backgroundColor="rgb(229, 229, 229)"
              width="100%"
              borderRadius="3px"
              border="solid 1px black"
              padding="5px 5px 10px 5px"
              gap="1rem !important"
              display="flex"
              justifyContent="flex-start"
              pos="relative"
            >
              {
                activeProfile.full_name === item.full_name
                  ? (
                    <Stack
                      width="15px"
                      height="15px"
                      right="3"
                      top="2"
                      cursor="pointer"
                      pos="absolute"
                      onClick={() => {
                        setEditCommentData({})
                        setEditCommentData(item)
                      }}
                    >
                      <EditIcon2 />
                    </Stack>
                  ) : ''
              }
              <Stack
                alignItems="center"
                display="inline"
                width="auto"
                justifyContent="flex-start"
                marginLeft="auto"
                marginRight="auto"
              >
                <Skeleton
                  isLoaded={loadedProfileImage[item.id]}
                  fadeDuration={1}
                  boxSize="55px"
                  position="relative"
                  objectFit="cover"
                  borderRadius="full"
                  display="inline-flex"
                  verticalAlign="bottom"
                >
                  <Image
                    src={item.profile_picture || ImagesPng.DefaultImage}
                    alt="User Image"
                    borderRadius="full"
                    cursor={
                      item?.coordinates
                      && item?.coordinates.length
                      && GoToMarker
                        ? 'pointer'
                        : 'auto'
                    }
                    onClick={() => {
                      if (
                        item?.coordinates
                        && item?.coordinates.length
                        && GoToMarker
                      ) {
                        GoToMarker(item)
                      }
                    }}
                    onLoad={() => handleProfileImage(item.id)}
                  />
                </Skeleton>
                <Text
                  display="inline-flex"
                  fontSize="16px"
                  lineHeight="28px"
                  wordBreak="break-word"
                  margin="10px 0 0 10px !important"
                >
                  {item.full_name}
                  <br />
                  { postTypeStatus({ item, noLabel: true }) }
                </Text>
              </Stack>

              <Stack width="98%" mx="auto">
                <Box
                  whiteSpace="pre-wrap"
                  fontSize="19px"
                  transition="font-size 0.8s"
                >
                  {item.body}
                </Box>
              </Stack>

              <Stack
                width="98%"
                height="29px"
                direction="row"
                border="solid 1px black"
                borderRadius="5px"
                padding="1.5px 15px"
                justifyContent="space-around"
                background={COLORS.whiteYellowGradientTopToBot}
                gap="0"
                mx="auto"
              >
                <LikeButtonAnimation className={animate[item.id] ? 'animate' : ''}>
                  <Stack
                    as="button"
                    onClick={() => {
                      if (!item?.likeOngoing) {
                        animateLike(item.id)
                        dispatch(setEventCommentLikeOngoing({ id: item.id }))
                        dispatch(likeEventComment(item.id))
                      }
                    }}
                  >
                    <LikeIcon fill={item.likedByMe ? COLORS.gold : 'none'} />
                  </Stack>
                  <Text fontSize="15px">{item.likes}</Text>
                </LikeButtonAnimation>

                <Stack
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                >
                  <Stack
                    as="button"
                    onClick={() => {
                      dispatch(setEventCommentTree([...eventCommentTree, item]))
                      dispatch(setEventComments([]))
                    }}
                  >
                    <CommentIcon />
                  </Stack>
                  <Text fontSize="15px">{item.comments}</Text>
                </Stack>

                <Stack
                  display="none"
                  flexDirection="row"
                  alignItems="center"
                >
                  <RepostIcon />
                  <Text fontSize="15px">0</Text>
                </Stack>

                <Stack
                  display="none"
                  flexDirection="row"
                  alignItems="center"
                >
                  <ShareIcon />
                  <Text fontSize="15px">0</Text>
                </Stack>
              </Stack>
            </MotionStack>
          ))
          : (
            <Stack>
              {
                getEventCommentsStatus === PENDING && !eventComments.length
                  ? ''
                  : (
                    <MotionStack
                      key="no-comment-placeholder"
                      initial={{ x: 450 }}
                      animate={{ x: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 60
                      }}
                    >
                      <Box
                        textAlign="center"
                        backgroundColor="rgb(229, 229, 229)"
                        borderRadius="3px"
                        border="solid 1px black"
                        padding="10px"
                        width="100%"
                        fontSize="13px"
                        transition="font-size 0.8s"
                      >
                        Be the first one to comment
                      </Box>
                    </MotionStack>
                  )
              }
            </Stack>
          )
      }
      {
        loadingMore && getEventCommentsStatus === PENDING
          ? (
            <Stack margin="75px 0 55px 0">
              <LoadingSpinier />
            </Stack>
          ) : ''
      }
    </Stack>
  )
}
